import { z } from 'zod';
import { PaginationMetaSchema } from './pagination.js';

export const ContactMessageStatusSchema = z.enum(['OPEN', 'READ', 'CLOSED']);
export type ContactMessageStatus = z.infer<typeof ContactMessageStatusSchema>;

export const ContactCategorySchema = z.enum([
  'DELETE_ACCOUNT',
  'QUESTION',
  'SUPPORT',
  'FEEDBACK',
  'OTHER',
]);
export type ContactCategory = z.infer<typeof ContactCategorySchema>;

/**
 * Sender slice surfaced to the admin viewer. Mirrors the public profile
 * fields a SUPER_ADMIN needs to act on the message (reach the user back,
 * see their role/scope).
 */
export const ContactMessageSenderSchema = z.object({
  id: z.string().uuid(),
  username: z.string(),
  fullName: z.string(),
  email: z.string().nullable(),
  cod: z.string().nullable(),
  role: z.string(),
  isActive: z.boolean(),
});
export type ContactMessageSender = z.infer<typeof ContactMessageSenderSchema>;

export const ContactMessageDtoSchema = z.object({
  id: z.string().uuid(),
  category: ContactCategorySchema,
  title: z.string(),
  body: z.string(),
  status: ContactMessageStatusSchema,
  createdAt: z.string(),
  readAt: z.string().nullable(),
  /** Reply-to address from the form (or the sender's account email). */
  contactEmail: z.string().nullable(),
  /** Only for DELETE_ACCOUNT: the account the requester wants removed. */
  requestedUsername: z.string().nullable(),
  /** Admin who marked the message as read; null if untouched. */
  readBy: z
    .object({ id: z.string().uuid(), fullName: z.string(), username: z.string() })
    .nullable(),
  /** Null for anonymous submissions from the public /contact form. */
  sender: ContactMessageSenderSchema.nullable(),
});
export type ContactMessageDto = z.infer<typeof ContactMessageDtoSchema>;

export const ContactMessageCreateInputSchema = z
  .object({
    category: ContactCategorySchema.default('OTHER'),
    title: z.string().trim().min(3, 'Title is too short').max(200),
    body: z.string().trim().min(10, 'Description is too short').max(5000),
    contactEmail: z.string().trim().email('Invalid email').max(255).optional(),
  })
  .strict();
export type ContactMessageCreateInput = z.infer<typeof ContactMessageCreateInputSchema>;

/**
 * Public (unauthenticated) submission from the /contact page. Email is
 * required so the admin can reply; requestedUsername is required only when
 * the category is DELETE_ACCOUNT.
 */
export const PublicContactMessageCreateInputSchema = z
  .object({
    category: ContactCategorySchema,
    title: z.string().trim().min(3, 'Title is too short').max(200),
    body: z.string().trim().min(10, 'Description is too short').max(5000),
    contactEmail: z.string().trim().email('Invalid email').max(255),
    requestedUsername: z.string().trim().min(1).max(150).optional(),
  })
  .strict()
  .refine((v) => v.category !== 'DELETE_ACCOUNT' || !!v.requestedUsername, {
    message: 'Username is required for account deletion requests',
    path: ['requestedUsername'],
  });
export type PublicContactMessageCreateInput = z.infer<
  typeof PublicContactMessageCreateInputSchema
>;

export const ContactMessageListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  status: ContactMessageStatusSchema.optional(),
  category: ContactCategorySchema.optional(),
});
export type ContactMessageListQuery = z.infer<typeof ContactMessageListQuerySchema>;

export const ContactMessageListResponseSchema = z.object({
  items: z.array(ContactMessageDtoSchema),
  pagination: PaginationMetaSchema,
  /** Open-status count, surfaced for the sidebar badge. */
  openCount: z.number().int().nonnegative(),
});
export type ContactMessageListResponse = z.infer<typeof ContactMessageListResponseSchema>;

export const ContactMessagePatchInputSchema = z
  .object({
    status: ContactMessageStatusSchema,
  })
  .strict();
export type ContactMessagePatchInput = z.infer<typeof ContactMessagePatchInputSchema>;
